/**
 * Application credits (PAYG) helpers for the pricing page (TP-187).
 *
 * On production, PAYG is visible only to users in featureFlagOverrideUserIds
 * until paygCreditsPricingPage is enabled for everyone.
 */

import featureToggles from 'featureToggles';
import { getPaygCreditsVariant } from 'helpers/PaygCreditsTestHelper';

export const APPLICATION_CREDITS_BUNDLE_SIZE = 12;
export const APPLICATION_CREDITS_EXPIRY_DAYS = 30;
export const PRO_ANNUAL_APPLICATIONS_PER_YEAR = 50 * 12;
export const PRICING_CREATIVE_PATH = '/pricing/creative';
export const LOW_CREDITS_THRESHOLD = 3;
export const EXPIRING_SOON_DAYS = 3;

export const APPLICATION_CREDITS_PRICING = {
  '£': '15.99',
  $: '19.99',
};

export const APPLICATION_CREDITS_PRICING_DISPLAY = {
  GBP: '£15.99',
  USD: '$19.99',
};

/**
 * Whether the user should see PAYG purchase UI on the pricing page.
 *
 * @param {{ id: number }|null|undefined} user
 * @returns {boolean}
 */
/**
 * Whether the user should see the PAYG purchase section on the pricing page.
 *
 * Requires the feature toggle to be on (or the user to be in the override list),
 * AND the user must not be in the A/B test control variant.
 *
 * @param {{ id: number, joined?: string }|null|undefined} user
 * @returns {boolean}
 */
export const isPaygCreditsPricingEnabled = (user) => {
  if (!user?.id) return false;

  const isOverrideUser = featureToggles.featureFlagOverrideUserIds.includes(
    user.id
  );

  const featureEnabled =
    !/production/.test(__CLOUD_ENV__) ||
    featureToggles.paygCreditsPricingPage ||
    isOverrideUser;

  if (!featureEnabled) return false;

  // Override users bypass the A/B test so they can always verify PAYG.
  if (isOverrideUser) return true;

  // When the A/B test is active, only treatment ('b') sees PAYG.
  return getPaygCreditsVariant(user) === 'b';
};

/**
 * Map CurrencySelector value to API currency code.
 *
 * @param {{ value: string }} currency
 * @returns {'gbp'|'usd'}
 */
export const getApplicationCreditsCurrencyCode = (currency) =>
  currency?.value === '£' ? 'gbp' : 'usd';

/**
 * @param {{ value: string }} currency
 * @returns {string}
 */
export const getApplicationCreditsPrice = (currency) =>
  APPLICATION_CREDITS_PRICING[currency?.value] || APPLICATION_CREDITS_PRICING.$;

/**
 * @param {{ value: string }} currency
 * @returns {string}
 */
export const getApplicationCreditsPerApplication = (currency) =>
  (
    parseFloat(getApplicationCreditsPrice(currency)) /
    APPLICATION_CREDITS_BUNDLE_SIZE
  ).toFixed(2);

/**
 * @param {string|number} yearPrice
 * @returns {string}
 */
export const getProAnnualPerApplication = (yearPrice) =>
  (parseFloat(yearPrice) / PRO_ANNUAL_APPLICATIONS_PER_YEAR).toFixed(2);

/**
 * @param {string} paygPerApplication
 * @param {string|number} yearPrice
 * @returns {number}
 */
export const getProAnnualSavingsMultiplier = (paygPerApplication, yearPrice) =>
  Math.max(
    2,
    Math.round(
      parseFloat(paygPerApplication) /
        (parseFloat(yearPrice) / PRO_ANNUAL_APPLICATIONS_PER_YEAR)
    )
  );

const getCheckoutOrigin = () =>
  typeof location !== 'undefined' ? location.origin : '';

/**
 * @returns {{ success_url: string, cancel_url: string }}
 */
export const getPricingCreativeCheckoutUrls = () => ({
  success_url: `${getCheckoutOrigin()}${PRICING_CREATIVE_PATH}?payment-credits-success`,
  cancel_url: `${getCheckoutOrigin()}${PRICING_CREATIVE_PATH}?payment-credits-failure`,
});

export const SETTINGS_SUBSCRIPTION_PATH = '/settings#subscription';

export const getSettingsSubscriptionCheckoutUrls = () => ({
  success_url: `${getCheckoutOrigin()}/settings?payment-credits-success#subscription`,
  cancel_url: `${getCheckoutOrigin()}/settings?payment-credits-failure#subscription`,
});

export const APPLICATION_CREDITS_CURRENCY_GBP = {
  label: 'GBP (£)',
  value: '£',
};
export const APPLICATION_CREDITS_CURRENCY_USD = {
  label: 'USD ($)',
  value: '$',
};

export function daysUntil(dateStr) {
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

export function isPackExpired(pack) {
  if (!pack?.expires_at) return false;
  return daysUntil(pack.expires_at) <= 0;
}

export function isPackInactive(pack) {
  return pack.credits_remaining === 0 || isPackExpired(pack);
}

export function formatCreditDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function getCreditPacks(user) {
  return (user.application_credits && user.application_credits.balances) || [];
}

export function getPurchaseHistory(user) {
  return (user.application_credits && user.application_credits.purchases) || [];
}

export function getRecentPurchaseHistory(user, limit = 5) {
  return getPurchaseHistory(user).slice(0, limit);
}

export function currencyCodeToSelector(currencyCode) {
  return String(currencyCode || 'usd').toLowerCase() === 'gbp'
    ? APPLICATION_CREDITS_CURRENCY_GBP
    : APPLICATION_CREDITS_CURRENCY_USD;
}

export function getApplicationCreditsCurrencyForUser(user) {
  const latestPurchase = getPurchaseHistory(user)[0];
  if (latestPurchase?.currency) {
    return currencyCodeToSelector(latestPurchase.currency);
  }

  return APPLICATION_CREDITS_CURRENCY_USD;
}

export function getApplicationCreditsPriceDisplay(currency) {
  const selector =
    typeof currency === 'object' && currency?.value
      ? currency
      : currencyCodeToSelector(currency);
  return `${selector.value}${getApplicationCreditsPrice(selector)}`;
}

export function getApplicationCreditsPriceForUser(user) {
  return getApplicationCreditsPriceDisplay(
    getApplicationCreditsCurrencyForUser(user)
  );
}

export function getTotalCreditsPurchased(packs) {
  return packs.reduce((sum, pack) => sum + pack.credits_purchased, 0);
}

export function formatPackPrice(pack) {
  if (pack?.amount && pack?.currency) {
    const symbol = pack.currency.toUpperCase() === 'GBP' ? '£' : '$';
    const value = (pack.amount / 100).toFixed(pack.amount % 100 === 0 ? 0 : 2);
    return `${symbol}${value}`;
  }

  const currency = (pack?.currency || 'USD').toUpperCase();
  return (
    APPLICATION_CREDITS_PRICING_DISPLAY[currency] ||
    APPLICATION_CREDITS_PRICING_DISPLAY.USD
  );
}

export function getDefaultPackPrice(user) {
  return getApplicationCreditsPriceForUser(user);
}
